// Angular modules.
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { FreudConfirmationService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { dummyUserId } from 'app/constants';
// Zenklub enums.
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import {
	IBusySlot,
	RecurrencyRemoveRange,
} from 'app/interfaces/availability.interface';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
// Zenklub interfaces.
import { EventPrice, ICalendarEvent } from 'app/interfaces/event.interface';
import {
	AppointmentSidebarFrom,
	ISidebar,
} from 'app/interfaces/sidebar.interface';
import { ProfessionalSubscriptionResponse } from 'app/interfaces/subscription.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { AvailabilitiesService } from 'app/services/availabilities.service';
// Zenklub services.
import { ChatService } from 'app/services/chat.service';
import { ContactsService } from 'app/services/contacts.service';
import { CurrencyService } from 'app/services/currency.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { SubscriptionService } from 'app/services/subscription.service';
import { CheckTriage, TriageService } from 'app/services/triage.service';
import { UserService } from 'app/services/user.service';
import { ZkuService } from 'app/services/zku.service';
import { changeAnimation, enterAnimation } from 'app/utils/animations.util';
import { DateTime } from 'luxon';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
import { IJourneySessionProgress } from 'modules/journeys/interfaces/journey.interface';
import { JourneyService } from 'modules/journeys/services/journey.service';
import { SessionService } from 'modules/video-conference/services/session/session.service';
import * as moment from 'moment';
// Third-party modules.
import { Observable, Subscription, forkJoin } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import { PromptuarySidebarService } from './../../../modules/promptuary/services/sidebar/promptuary-sidebar.service';
import { IChatUser } from '../../interfaces/chatuser.interface';
import {
	ITaxonomy,
	ITaxonomyGroupContext,
	ITaxonomyGroupResponse,
} from '../../interfaces/search.interface';
import { TaxonomyService } from '../../services/taxonomy.service';

@Component({
	selector: 'app-details',
	templateUrl: './details.component.html',
	styleUrls: ['./details.component.scss'],
	animations: [changeAnimation, enterAnimation],
})
export class DetailsComponent implements OnChanges, OnInit {
	// Inputs.
	@Input() activeSidebar: ISidebar;
	// Outputs.
	@Output() showEvents: EventEmitter<ISidebar> = new EventEmitter<ISidebar>();
	@Output()
	scheduleEvent: EventEmitter<ISidebar> = new EventEmitter<ISidebar>();
	@Output()
	rescheduleEvent: EventEmitter<ISidebar> = new EventEmitter<ISidebar>();
	@Output() changeHeight: EventEmitter<any> = new EventEmitter<any>();
	@Output() refreshEvents: EventEmitter<boolean> = new EventEmitter<boolean>();
	// Event.
	event: ICalendarEvent;
	eventFound = false;
	busySlot: IBusySlot;
	busySlotFound = false;
	showConfirmSessionFinished = false;
	// Status.
	isLoading = true;
	isLoadingSave = false;
	isLoadingEvents = false;
	isLoadingReasons = false;
	isValidatingStart = false;
	isReschedule = false;
	allowConference = false;
	allowReschedule = false;
	dataUpdated: string;
	showChatButton = false;
	// Update control.
	lastUpdatedAt: Date;
	// Labels.
	duration: number;
	screenDate: string;
	startHours: string;
	originalDateFrom: string;
	originalDateTo: string;
	originalStartHours: string;
	endHours: string;
	utcInfo: string;
	sessionStatus: string;
	appointDate: string;
	tag: number;
	professionalProfit: EventPrice;
	// Size control.
	paddingName = '15px 0';
	// Listener for new events.
	newEvSubscription: Subscription;
	// reasons
	selectedReason = '0';
	selectedReasons: (ITaxonomyGroupResponse | ITaxonomy)[] = [];
	reasons: ITaxonomyGroupResponse[] = [];
	showReasonsModal = false;
	eventId: string;
	userId: string;
	strAreYouSure: string;
	sessionModel = false;

	voucherDescription: string;
	isFirstSession;
	isPremium = false;
	isSosRs = false;

	triage$: Observable<CheckTriage>;
	hasTriage = false;
	triage: CheckTriage & { mainComplaintText: string };
	showTriageData = false;
	isProfessionalZen = false;
	contact;
	screeningResult: string;
	journeySessionProgress: IJourneySessionProgress;
	userType;
	confirmDeleteBusySlot = false;
	selectedOptionToDeleteBusySlot: RecurrencyRemoveRange =
		RecurrencyRemoveRange.ONLY;
	// Constructor method.
	constructor(
		private actionTrackerService: ActionTrackerService,
		protected chat: ChatService,
		protected currency: CurrencyService,
		private contacts: ContactsService,
		protected dateTime: DateTimeService,
		protected events: EventsService,
		protected global: GlobalService,
		protected notifications: ZkNotificationService,
		protected translate: TranslateService,
		protected user: UserService,
		protected availabilityService: AvailabilitiesService,
		protected taxonomy: TaxonomyService,
		protected router: Router,
		protected subscriptionService: SubscriptionService,
		protected translateService: TranslateService,
		protected triageService: TriageService,
		protected zkuService: ZkuService,
		private confirmationService: FreudConfirmationService,
		private journeyService: JourneyService,
		private sessionService: SessionService,
		private promptuarySidebarService: PromptuarySidebarService,
		public sidebarService: AppointmentSidebarService
	) {
		const userService = this.zkuService.getZku();
		if (
			userService !== undefined &&
			userService?.tags?.some((tag) => tag['id'] === 'professional_zen')
		) {
			this.isProfessionalZen = true;
		}
	}

	// On init.
	ngOnInit() {
		this.events.eventEmitter().subscribe((event) => {
			if (
				this.activeSidebar &&
				this.activeSidebar.id &&
				event.id === this.activeSidebar.id &&
				event.status !== event.status
			) {
				this.getEvent(this.activeSidebar.id, false);
			}
			this.isLoading = false;
		});

		this.subscriptionService.getSubscriptionList().subscribe(
			(response: ProfessionalSubscriptionResponse) => {
				this.isPremium = response.subscriptions.some((sub) => {
					return [
						'active',
						'requested_cancelation',
						'cancelation_accepted',
					].includes(sub.status);
				});
				this.isLoading = false;
				if (this.busySlot) {
					this.actionTrackerService.eventTracker(
						SCOPE_TYPE.Appointment,
						SUBSCOPE_TYPE.Details,
						ITEM_TYPE.Modal,
						INTERACTION_TYPE.Seen,
						{
							type: 'particular',
						}
					);
				}
			},
			(error: ApiErrorResponse) => {
				this.notifications.showError(
					error.toString(),
					this.translateService.instant('PAGES.MEMBERSHIP.MESSAGES.ERROR')
				);
				this.isLoading = false;
			}
		);
	}

	// On changes.
	ngOnChanges() {
		this.eventFound = false;
		this.busySlotFound = false;

		if (this.activeSidebar.type === 'busyslot') {
			this.getBusySlot(this.activeSidebar.id);
		} else if (
			this.activeSidebar.id &&
			this.activeSidebar.type === 'to-confirm'
		) {
			this.showReasonsModal = true;
			// Get event.
			this.getEvent(this.activeSidebar.id);
		} else if (this.activeSidebar.id) {
			// Get event.
			this.getEvent(this.activeSidebar.id);
		}
	}

	getCurrentUserScreening(profileId: string): Promise<void> {
		return this.journeyService
			.getUserScreening(profileId)
			.toPromise()
			.then((res) => {
				if (res.found) {
					this.screeningResult = res.label;
				}
			})
			.catch(() => {
				return;
			});
	}

	getJourneySessionProgress(profileId: string, sessionId: string): void {
		this.journeyService
			.getJourneySessionProgress(profileId, sessionId)
			.subscribe((journeySessionProgress: IJourneySessionProgress) => {
				this.journeySessionProgress = journeySessionProgress;
			});
	}

	//TODO: remover essa validaçao quando disponibilizarmos recibo para os anos anteriores a 2022
	isSessionThisYear(sessionDate) {
		const sessionYear = moment(sessionDate).year();
		return sessionYear >= 2021 ? true : false;
	}

	showReceiptButton() {
		if (
			this.dateTime.dateTimeIsAfter(this.event?.dayEnd) ||
			!this.event?.isB2C
		) {
			return false;
		}
		return (
			this.event?.status === 'paid' &&
			(this.event?.price > 0 || this.event?.b2cPaidPrice > 0) &&
			this.isSessionThisYear(this.event?.dayEnd)
		);
	}

	// Show today events.
	private showTodayEvents(): void {
		// Update sidebar info.
		this.activeSidebar.id = null;
		this.activeSidebar.mode = ESidebarMode.Appointments;
		// Show today events.
		this.showEvents.emit(this.activeSidebar);
	}

	// get busy slot
	private getBusySlot(id: string, showLoader = true): void {
		this.isLoading = showLoader;
		this.availabilityService.getBusySlot(id).then((res) => {
			if (!res) {
				this.busySlotFound = false;
				this.isLoading = false;
				return;
			}
			this.busySlotFound = true;
			this.isLoading = false;
			this.busySlot = res;
			const startD: Date = new Date(this.busySlot.startDate);
			// Get unabbreviated date for session.
			this.screenDate = this.dateTime.getInFullDate(startD);
			// Date and time info.
			const endD: Date = new Date(this.busySlot.endDate);
			const startH: number = startD.getHours();
			const startM: number = startD.getMinutes();
			const endH: number = endD.getHours();
			const endM: number = endD.getMinutes();

			this.startHours = this.dateTime.getFormatedHoursAlt(startH, startM);
			this.endHours = this.dateTime.getFormatedHoursAlt(endH, endM);
			this.utcInfo = this.dateTime.getBrowserTimezone();
		});
	}

	// removes a busy slot
	public deleteBusySlot(
		id: string,
		recurrencyId?: IBusySlot['recurrencyId']
	): void {
		this.toggleConfirmDeleteBusySlot(false);
		this.isLoading = true;
		this.availabilityService
			.removeBusySlot(id, recurrencyId, this.selectedOptionToDeleteBusySlot)
			.subscribe(() => {
				this.isLoading = false;
				this.busySlotFound = false;
				// Force to refresh events.
				this.refreshEvents.emit(true);
				this.showTodayEvents();
			});
	}

	// Get and event.
	private getEvent(id: string, showLoader = true): void {
		// Loading.
		this.isLoading = showLoader;
		// Get event.
		this.events.getEvent(id, false).then(async (res) => {
			if (res) {
				this.isSosRs = this.eventIsPartOfSosRs(res);

				if (res.subscription?.coordinatedCareType === 'screening') {
					await this.getCurrentUserScreening(res.profileId);
				}

				if (res.subscription?.coordinatedCareType === 'therapy_sessions') {
					this.getJourneySessionProgress(res.profileId, res.id);
				}
				// Event found.
				this.eventFound = true;
				// Store event.
				this.event = res;
				this.actionTrackerService.eventTracker(
					SCOPE_TYPE.Appointment,
					SUBSCOPE_TYPE.Details,
					ITEM_TYPE.Modal,
					INTERACTION_TYPE.Seen,
					{
						type: this.event.professionalAddress ? 'presencial' : 'online',
					}
				);
				this.event.userImageUrl = await this.contacts.getContactImage(res);
				this.sessionService
					.getData(this.event.id)
					.toPromise()
					.then((session) => {
						this.isFirstSession = session.numberOfSessions === 1;
					});

				this.contacts
					.getContact(this.event.contactId)
					.toPromise()
					.then((contact) => {
						this.contact = contact;
					});
				// duration
				this.duration = res.isHalfHourSession ? 30 : 50;

				// Check if appointment is not confirmed and it has already started
				const now = new Date().getTime();
				const appointmentStartTime = new Date(this.event.day).getTime();
				this.showConfirmSessionFinished =
					this.event.status === 'paid' && now > appointmentStartTime;

				if (this.event.profileId && this.event.profileId !== dummyUserId) {
					this.showChatButton = true;
				}
				// Store last update date.
				this.lastUpdatedAt =
					this.event.updatedAt || this.event.createdAt || new Date();
				// Allow reschedule?
				this.allowReschedule = this.events.allowReschedule(this.event);
				// this.allowReschedule = this.event.canBeRescheduled;
				// Get event date.
				const startD: Date = new Date(this.event.day);
				// Get event status.
				this.allowConference = this.event.status === 'paid';
				this.showChatButton =
					this.event.status === 'reserved' ? false : this.showChatButton;
				// Force reset of original dates (reschedule only).
				this.originalDateFrom = null;
				this.originalDateTo = null;
				if (
					this.event.reschedule &&
					this.event.reschedule.rescheduleUtcStartDate
				) {
					// Reschedule to date.
					if (this.event.status === 'reschedule') {
						const offset: number = this.dateTime.getTimezoneOffset();
						const newDate: Date = this.dateTime.getLocalDate(
							this.event.reschedule.rescheduleUtcStartDate,
							offset
						);
						this.originalDateTo = this.dateTime.getTranslatedDate(
							newDate,
							true
						);
					}
					// Rescheduled from date.
					if (
						this.event.status === 'reschedule_request' ||
						this.event.status === 'paid'
					) {
						const oldDate: Date = new Date(
							this.event.reschedule.rescheduleUtcStartDate
						);
						this.originalDateFrom = this.dateTime.getTranslatedDate(
							oldDate,
							true
						);
					}
				}

				// Get unabbreviated date for session.
				this.screenDate = this.dateTime.getInFullDate(startD);
				this.utcInfo = this.dateTime.getBrowserTimezone();

				// Voucher value
				this.voucherDescription = this.event.voucherDescription;

				// Contact reason value
				this.mapCurrentReasons();

				// Doctor's profit.
				this.professionalProfit = this.events.getEventPriceToShow(this.event);
				// Padding control.
				this.paddingName =
					this.event.userName && this.event.userName.length > 17
						? '7px 0'
						: '15px 0';
				// Date of appointment.
				this.appointDate = this.dateTime.getInFullDate(
					new Date(this.event.createdAt)
				);
				// Loaded.
				this.isLoading = false;
				// Show animation.
				if (!showLoader) {
					this.dataUpdated = new Date().toString();
				}
				// Change calendar height.
				this.changeHeight.emit();
				// Scroll if needed.
				this.goToSidebar();

				// Check Triage status.
				if (this.event.triageId) {
					this.checkTriage();
				}
			} else {
				// Event not found.
				this.event = null;
				this.eventFound = false;
				// Loaded.
				this.isLoading = false;
			}
		});
	}

	private mapCurrentReasons = () => {
		if (this.event) {
			forkJoin([
				this.taxonomy.getTaxonomyList('session-outcome'),
				this.taxonomy.getTaxonomyGroup(ITaxonomyGroupContext.SessionReason),
			]).subscribe({
				next: ([sessionOutcomes, sessionReasons]) => {
					this.reasons = [
						...sessionReasons.reduce(
							(total, area) => [...total, ...area.children],
							[]
						),
						...sessionOutcomes.map(({ id, name }) => ({
							taxonomyId: id,
							name,
						})),
					];
					const auxReasons = this.event.contactReason || [];
					this.selectedReasons = auxReasons
						.map((auxId) => {
							return this.reasons.find((reason) => reason.taxonomyId === auxId);
						})
						.filter((t) => t);
				},
			});
		}
	};

	public goToPromptuary() {
		this.router.navigate([
			`promptuary/${this.activeSidebar.id}/${this.event.profileId}`,
		]);
	}

	public trackPromptuaryListRedirect() {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Caledar,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.BtnPromptuaryList,
			INTERACTION_TYPE.Click,
			{}
		);
	}

	public trackPromptuaryRedirect() {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Caledar,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.BtnPromptuary,
			INTERACTION_TYPE.Click,
			{}
		);
	}

	public openReasonsModal() {
		this.showReasonsModal = true;
	}

	public onReasonsCancel() {
		this.showReasonsModal = false;
	}

	public onReasonsSave() {
		this.getEvent(this.event.id, true);
		this.showReasonsModal = false;
	}

	// Schedule event.
	public schedule(): void {
		this.activeSidebar.from = AppointmentSidebarFrom.SCHEDULE_MODAL;
		this.scheduleEvent.emit({
			...this.activeSidebar,
			contactId: this.event.contactId,
			id: null,
		});
	}

	// Reschedule event.
	public reschedule(): void {
		if (this.allowReschedule) {
			this.rescheduleEvent.emit(this.activeSidebar);
		}
	}

	// Cancel invite.
	public async cancelInvite() {
		this.eventId = this.activeSidebar.id;
		this.userId = await this.user.getUserId();
		if (this.eventId && this.userId) {
			this.translate
				.get(['SCHEDULE.QUESTIONS.CANCELTHISINVITE', 'OPERATIONS.CANCELINVITE'])
				.subscribe((res) => {
					const strAreYouSure: string =
						res['SCHEDULE.QUESTIONS.CANCELTHISINVITE'];
					const strCancelTitle: string = res['OPERATIONS.CANCELINVITE'];
					this.confirmationService.confirm({
						message: strAreYouSure,
						header: this.translateService.instant('OPERATIONS.CONFIRM_CANCEL'),
						acceptLabel: this.translateService.instant('OPERATIONS.CONFIRM'),
						rejectLabel: this.translateService.instant('OPERATIONS.CLOSE'),
						accept: () => {
							this.events.cancelInvite(this.eventId).subscribe(
								() => {
									this.actionTrackerService.eventTracker(
										SCOPE_TYPE.Session,
										SUBSCOPE_TYPE.Invite,
										ITEM_TYPE.Cancel,
										INTERACTION_TYPE.Click,
										{
											type: this.event.professionalAddress
												? 'face-to-face'
												: 'online',
										}
									);
									this.actionTrackerService.eventTracker(
										SCOPE_TYPE.Appointment,
										SUBSCOPE_TYPE.Details,
										ITEM_TYPE.CancelInviteBtn,
										INTERACTION_TYPE.Click,
										{
											type: this.event.professionalAddress
												? 'face-to-face'
												: 'online',
										}
									);
									this.notifications.showSuccess(strCancelTitle);
									this.showTodayEvents();
								},
								(error) => {
									// Message.
									this.notifications.showError(error, strCancelTitle);
								}
							);
						},
					});
				});
		}
	}

	// Cancel invite.
	public async cancelReschedule() {
		const eventId: string = this.event.rescheduledFrom;
		const userId: string = await this.user.getUserId();
		if (eventId && userId) {
			this.translate
				.get([
					'SCHEDULE.QUESTIONS.CANCELTHISRESCHEDULE',
					'OPERATIONS.CANCELRESCHEDULE_SUCCESS',
				])
				.subscribe((res) => {
					this.strAreYouSure = res['SCHEDULE.QUESTIONS.CANCELTHISRESCHEDULE'];
					const strCancelTitle: string =
						res['OPERATIONS.CANCELRESCHEDULE_SUCCESS'];
					this.confirmationService.confirm({
						header: this.translateService.instant('OPERATIONS.CONFIRM_CANCEL'),
						acceptLabel: this.translateService.instant('OPERATIONS.CONFIRM'),
						rejectLabel: this.translateService.instant('OPERATIONS.CLOSE'),
						message: this.strAreYouSure,
						accept: () => {
							this.events.cancelReschedule(eventId).subscribe(
								() => {
									this.actionTrackerService.eventTracker(
										SCOPE_TYPE.Appointment,
										SUBSCOPE_TYPE.Details,
										ITEM_TYPE.CancelRescheduleBtn,
										INTERACTION_TYPE.Click,
										{}
									);
									this.notifications.showSuccess(strCancelTitle);
									this.refreshEvents.emit(true);
									this.closeEventSelected();
								},
								({ error }) => {
									this.notifications.showError(error.message, strCancelTitle);
								}
							);
						},
					});
				});
		}
	}

	// Start a new conference.
	public startConference(): void {
		if (this.event.status !== 'paid') {
			return;
		}

		this.router.navigate([`session/${this.activeSidebar.id}`]);
	}

	// Close selected event.
	public closeEventSelected(): void {
		this.showTodayEvents();
	}

	// Scrolls to details on mobile.
	goToSidebar(): void {
		// Scroll to form if on mobile.
		if (this.global.isMobileDevice()) {
			setTimeout(() => {
				const sideBar: HTMLElement = document.getElementById('apDetails-user');
				if (sideBar) {
					sideBar.scrollIntoView({ block: 'start', behavior: 'smooth' });
				}
			}, 100);
		}
	}

	getEventStatus(event: ICalendarEvent): string {
		switch (event.status.toLowerCase()) {
			case 'paid':
				if (event.confirmedAt) {
					return this.translateService.instant('SCHEDULE.STATUS.FULFILLED');
				}

				return this.translateService.instant('SCHEDULE.STATUS.CONFIRMED_YES');
			case 'reserved':
				return this.translateService.instant('SCHEDULE.STATUS.WAITINGPAYMENT');
		}

		return event.statusText;
	}

	getPaymentStatus(event: ICalendarEvent): string {
		if (this.originalDateFrom) {
			return `${this.translateService.instant('SCHEDULE.RESCHEDULEDFROM')} ${
				this.originalDateFrom
			}`;
		}

		switch (event.status.toLowerCase()) {
			case 'reschedule':
			case 'reschedule_request':
				return this.translateService.instant('SCHEDULE.STATUS.PAID');
		}

		return event.statusText;
	}

	getFinalCompensation(event: ICalendarEvent): number | false {
		const finalCompensation = event.compensationFinal;
		return finalCompensation && finalCompensation.compensation !== undefined
			? finalCompensation && finalCompensation.compensation
			: false;
	}

	async showChat(event: ICalendarEvent) {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Details,
			ITEM_TYPE.BtnChat,
			INTERACTION_TYPE.Click,
			{}
		);
		const chatUser: IChatUser = {
			id: event.profileId,
			photoUrl: event.userImageUrl,
			username: event.userName,
			role: this.chat.role,
		};
		await this.chat.startConversation(chatUser, 'appointment-modal');
	}

	checkTriage() {
		this.triage$ = this.triageService
			.checkTriage({
				professionalId: this.event?.professionalId,
				profileId: this.event?.profileId,
			})
			.pipe(
				take(1),
				tap((triage: CheckTriage) => (this.hasTriage = Boolean(triage)))
			);
	}

	formatTriageReasons() {
		return this.triage?.reasonsForTherapy?.reasons
			?.map((reason) => {
				if (typeof reason === 'string') return reason;
				return reason.OTHER;
			})
			.join(', ');
	}

	showTriage(triage: CheckTriage) {
		this.triage = {
			...triage,
			mainComplaintText: triage.mainComplaint?.join(','),
		};
		this.showTriageData = true;
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Caledar,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.BtnTriage,
			INTERACTION_TYPE.Click,
			{}
		);
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Details,
			ITEM_TYPE.ShowTriageBtn,
			INTERACTION_TYPE.Click,
			{}
		);
	}

	eventIsPartOfSosRs(event: ICalendarEvent): boolean {
		if (!event.subscription) {
			return false;
		}

		return event.subscription.sessionModel === 'Sessão SOS RS';
	}

	showAppointment() {
		this.showTriageData = false;
	}

	openPromptuarySidebar() {
		this.trackPromptuaryRedirect();
		this.promptuarySidebarService.openSidebarOrGoToPage(
			this.event.profileId,
			this.event.id,
			'appointment-modal'
		);
		this.closeEventSelected();
	}

	toggleConfirmDeleteBusySlot(open?: boolean) {
		this.confirmDeleteBusySlot =
			open === undefined ? !this.confirmDeleteBusySlot : open;
	}

	handleDeleteClick() {
		if (this.busySlot.recurrencyId) {
			this.toggleConfirmDeleteBusySlot(true);
			return;
		}
		this.deleteBusySlot(this.busySlot.id);
	}

	openSidebarToEditBusySlot(busySlot: IBusySlot) {
		const isBusySlot = true;
		this.sidebarService.setSidebar(
			true,
			{
				...busySlot,
				mode: ESidebarMode.Edit,
			},
			isBusySlot
		);
	}

	formatDate(date) {
		return DateTime.fromISO(date)
			.setLocale('pt-BR')
			.toFormat("dd 'de' MMMM 'às' HH:mm");
	}
}
